// Thin API client. A guest's credential lives in localStorage per meeting, so a guest who leaves,
// reloads or closes the tab comes back as the same guest (one entry for the host), not a new one.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

const credKey = (meetingId: string) => `meet-cred:${meetingId}`;
const inMemory = new Map<string, string>();
export const guestCred = {
  get: (id: string) => {
    try {
      // sessionStorage: credentials saved before this change, in a tab still open.
      return localStorage.getItem(credKey(id)) ?? sessionStorage.getItem(credKey(id)) ?? inMemory.get(id) ?? null;
    } catch {
      return inMemory.get(id) ?? null;
    }
  },
  set: (id: string, cred: string) => {
    inMemory.set(id, cred);
    try {
      localStorage.setItem(credKey(id), cred);
    } catch {
      /* storage blocked: the credential lives only in memory for this page */
    }
  },
};

export async function api<T>(path: string, init: { method?: string; body?: unknown; meetingId?: string } = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  const cred = init.meetingId ? guestCred.get(init.meetingId) : null;
  if (cred) headers['Authorization'] = `Guest ${cred}`;
  const res = await fetch(path, {
    method: init.method ?? (init.body === undefined ? 'GET' : 'POST'),
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
    credentials: 'same-origin',
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new ApiError(res.status, data.error ?? `Request failed (${res.status}).`);
  return data;
}

export interface Session {
  user: string;
  username: string;
  displayName: string;
  role: string;
  mustChangePassword: boolean;
}

export interface MeetingInfo {
  id: string;
  title: string;
  hostName: string;
  status: 'open' | 'ended';
  waitingRoom: boolean;
}

/** A file on the stage (server state, mirrored in room metadata). */
export interface Presentation {
  by: string;
  byName: string;
  versionId: string;
  name: string;
  page: number;
  at: number;
}

export interface Me {
  status: 'open' | 'ended';
  endReason: string | null;
  state: 'waiting' | 'admitted' | 'denied';
  identity: string;
  name: string;
  host: boolean;
  title: string;
  idleWarning: boolean;
  idleEndsAt: number;
  waiting?: number;
  workspaceId?: string | null;
  canDownload?: boolean;
  canUpload?: boolean;
  presenting?: Presentation | null;
}

export interface Snapshot {
  id: string;
  title: string;
  link: string;
  status: 'open' | 'ended';
  waitingRoom: boolean;
  createdAt: number;
  present: string[];
  idleWarning: boolean;
  idleEndsAt: number;
  guests: { id: string; name: string; state: 'waiting' | 'admitted' | 'denied'; knockedAt: number; canDownload: boolean; uploadAll: boolean; issue?: { phase: string; source: string; name: string; at: number } | null; present?: boolean; stale?: boolean; leftAt?: number | null }[];
  workspaceId: string | null;
  uploads: { id: string; guestId: string; guestName: string; name: string; size: number }[];
}

export const endReasonText: Record<string, string> = {
  host: 'The host ended the meeting.',
  idle: 'The meeting ended after 30 minutes without activity.',
  empty: 'The meeting ended after everyone left.',
  cap: 'The meeting reached its 4-hour limit.',
};
